import { StowagePlanChangeBM } from 'src/types/BusinessModel/Stowage/StowagePlanChangeBM';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { UUID } from 'crypto';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getChangeDataGuid } from 'src/utils/stowage/stowagePlanChangeUtils/stowageChangeDataTypeUtils';
import { createViolationChangeData } from './StowageChangeData/ViolationChangeDataFactory';
import { createPortCallChangeData } from './StowageChangeData/RotationPortCallChangeDataFactory';
import { createBerthCallChangeData } from './StowageChangeData/BerthCallChangeDataFactory';
import { createCargoOperationChangeData } from './StowageChangeData/CargoOperationChangeDataFactory';
import { createCargoChangeData } from './StowageChangeData/CargoChangeDataFactory';

const addStowagePlanChanges = (stowagePlanChanges, stowagePlanGuid, data, type, createChangeData, isDeleted) => {
    data.forEach((item) => {
        const changeDataGuid = getChangeDataGuid(type, item) ?? crypto.randomUUID();
        const stowagePlanChange = isDeleted
            ? createDeleteStowagePlanChange(stowagePlanGuid, changeDataGuid, type)
            : createStowagePlanChange(stowagePlanGuid, changeDataGuid, type, createChangeData(item));
        stowagePlanChanges.push(stowagePlanChange);
    });
};

export const addViolationsToStowagePlanChanges = (
    stowagePlanChanges: StowagePlanChange[],
    stowagePlanGuid: UUID,
    violations: Violation[],
    isDeleted: boolean
) => {
    addStowagePlanChanges(stowagePlanChanges, stowagePlanGuid, violations, StowageChangeDataType.Violation, createViolationChangeData, isDeleted);
};

export const addCargoesToStowagePlanChanges = (stowagePlanChanges, stowagePlanGuid, cargoes, isDeleted) => {
    addStowagePlanChanges(stowagePlanChanges, stowagePlanGuid, cargoes, StowageChangeDataType.RotationCargo, createCargoChangeData, isDeleted);
};

export const addPortCallsToStowagePlanChanges = (
    stowagePlanChanges: StowagePlanChange[],
    stowagePlanGuid: UUID,
    rotationPortCalls: RotationPortCall[],
    isDeleted: boolean
) => {
    addStowagePlanChanges(
        stowagePlanChanges,
        stowagePlanGuid,
        rotationPortCalls,
        StowageChangeDataType.RotationPortCall,
        createPortCallChangeData,
        isDeleted
    );

    rotationPortCalls.forEach((portCall) => {
        addBerthCallsToStowagePlanChanges(stowagePlanChanges, stowagePlanGuid, portCall.berthCalls, isDeleted);
    });
};
export const addBerthCallsToStowagePlanChanges = (
    stowagePlanChanges: StowagePlanChange[],
    stowagePlanGuid: UUID,
    rotationBerthCalls: RotationBerthCall[],
    isDeleted: boolean
) => {
    addStowagePlanChanges(
        stowagePlanChanges,
        stowagePlanGuid,
        rotationBerthCalls,
        StowageChangeDataType.RotationBerthCall,
        createBerthCallChangeData,
        isDeleted
    );

    rotationBerthCalls.forEach((berthCall) => {
        addCargoOperationsToStowagePlanChanges(stowagePlanChanges, stowagePlanGuid, berthCall.cargoOperations, isDeleted);
    });
};
export const addCargoOperationsToStowagePlanChanges = (
    stowagePlanChanges: StowagePlanChange[],
    stowagePlanGuid: UUID,
    rotationCargoOperations: RotationCargoOperation[],
    isDeleted: boolean
) => {
    addStowagePlanChanges(
        stowagePlanChanges,
        stowagePlanGuid,
        rotationCargoOperations,
        StowageChangeDataType.RotationCargoOperation,
        createCargoOperationChangeData,
        isDeleted
    );
};

export const createStowagePlanChange = (
    stowagePlanGuid: UUID,
    changeDataGuid: UUID,
    changeDataType: StowageChangeDataType,
    changedData?: any,
    shouldConvertUndefinedValuesToNull?: boolean
): StowagePlanChange => {
    return {
        stowagePlanChangeGuid: crypto.randomUUID(),
        stowagePlanGuid,
        changeDataGuid,
        changeDataType,
        isApproved: false,
        data: changedData
            ? shouldConvertUndefinedValuesToNull
                ? JSON.stringify(changedData, (_, value) => (value === undefined ? null : value))
                : JSON.stringify(changedData)
            : null,
        isDeleted: false,
    };
};

export const mapStowagePlanChange = (bm: StowagePlanChangeBM): StowagePlanChange => {
    return {
        stowagePlanChangeGuid: bm.stowagePlanChangeGuid,
        stowagePlanGuid: bm.stowagePlanGuid,
        changeDataGuid: bm.changeDataGuid,
        transactionGuid: bm.transactionGuid,
        changeDataType: bm.changeDataType,
        isApproved: bm.isApproved,
        isDeleted: bm.isDeleted,
        data: bm.data,
        lastUpdatedBy: bm.lastUpdatedBy,
        lastUpdatedUtc: bm.lastUpdatedUtc,
    };
};

export const mapStowagePlanChangeBM = (stowagePlanChange: StowagePlanChange, userName: string, stowagePlanGuid: UUID): StowagePlanChangeBM => {
    return {
        stowagePlanChangeGuid: stowagePlanChange.stowagePlanChangeGuid,
        stowagePlanGuid: stowagePlanGuid,
        changeDataGuid: stowagePlanChange.changeDataGuid,
        transactionGuid: stowagePlanChange.transactionGuid,
        changeDataType: stowagePlanChange.changeDataType,
        isApproved: stowagePlanChange.isApproved,
        isDeleted: stowagePlanChange.isDeleted,
        data: stowagePlanChange.data,
        lastUpdatedBy: userName,
        lastUpdatedUtc: getDateNow(),
    };
};

export const createDeleteStowagePlanChange = (
    stowagePlanGuid: UUID,
    changeDataGuid: UUID,
    stowageChangeDataType: StowageChangeDataType
): StowagePlanChange => {
    return {
        stowagePlanChangeGuid: crypto.randomUUID(),
        stowagePlanGuid,
        changeDataGuid,
        isDeleted: true,
        lastUpdatedUtc: getDateNow(),
        transactionGuid: crypto.randomUUID(),
        changeDataType: stowageChangeDataType,
        isApproved: false,
    };
};

export const mapStowagePlanChanges = (bms: StowagePlanChangeBM[]) => {
    return bms ? bms.map(mapStowagePlanChange) : [];
};

export const mapStowagePlanChangeBMs = (stowagePlanChanges: StowagePlanChange[], userName: string, stowagePlanGuid: UUID) => {
    return stowagePlanChanges ? stowagePlanChanges.map((s) => mapStowagePlanChangeBM(s, userName, stowagePlanGuid)) : [];
};
